import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TimeTrack } from '@/utils/planningTimeTracks'
import { CarrilTrack } from '@/components/planning/CarrilTrack'
import { INFO_COL_WIDTH } from '@/components/planning/ganttLayout'
import { seriesDeComponente, tramosDeSerie, type SerieComponente } from '@/utils/roturaComponente'
import { fmtDateShortLocale, fmtUds } from '@/utils/planningFormatters'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { MS_DIA } from '@/utils/planningDateRanges'
import type { PlanificacionSemanalRow } from '@/types'

/** Cuántas series se pintan. Buscar «ARF» casa con cientos de componentes y una
 *  franja de doscientas filas no se lee: las que sobran se cuentan en el pie. */
const MAX_SERIES = 6

/**
 * Cuándo rompe el componente buscado.
 *
 * El Gantt contesta «qué órdenes tienen rotura». Esta franja contesta la otra
 * pregunta, la que se hace quien compra: **qué día concreto me quedo sin este
 * material**. Va sobre el MISMO eje que las barras —misma rejilla, misma línea
 * de hoy—, así que la fecha se lee por dónde cae, sin tener que buscarla en un
 * texto.
 *
 * Cada fila es una combinación empresa × artículo × almacén, porque el saldo es
 * de esa combinación y no del código a secas.
 *
 * El color sale siempre de un dato: cada tramo lo abre un consumo y lleva el
 * estado de ESE consumo. Donde el origen no marca rotura, la franja no la pinta.
 *
 * ⚠ Cuenta **todas** las órdenes del rango que consumen el componente, también
 * las que el resto de filtros esconde. El stock se lo lleva quien se lo lleva:
 * mirar sólo las visibles correría la fecha de rotura hacia adelante, que es
 * justo la forma cara de equivocarse. El pie lo dice en voz alta.
 */
export function FranjaRotura({ planRows, casaComponente, track, dark }: {
  /** TODAS las filas del rango, sin filtrar. Ver el aviso de arriba. */
  planRows: PlanificacionSemanalRow[]
  casaComponente: (r: PlanificacionSemanalRow) => boolean
  track: TimeTrack<string>
  dark: boolean
}) {
  const { t, i18n } = useTranslation()
  const fmtArt = useFmtArticulo()

  const series = useMemo(
    () => seriesDeComponente(planRows, casaComponente),
    [planRows, casaComponente],
  )

  if (series.length === 0) return null

  const visibles = series.slice(0, MAX_SERIES)
  const ocultas = series.length - visibles.length
  const conRotura = series.filter(s => s.totalRoturas > 0).length

  const bdr      = dark ? 'border-white/10' : 'border-slate-200'
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const mainTxt  = dark ? 'text-slate-100' : 'text-slate-900'

  return (
    <div className={`border-b shrink-0 ${bdr} ${dark ? 'bg-slate-900/70' : 'bg-slate-50/70'}`}>
      {/* Rótulo de la franja */}
      <div className={`flex items-center border-b ${bdr}`}>
        <div className={`shrink-0 px-3 py-1.5 border-r ${bdr}`} style={{ width: INFO_COL_WIDTH }}>
          <span className={`text-[10px] font-bold uppercase tracking-wider ${mutedTxt}`}>
            {t('planning.rotura.titulo')}
          </span>
        </div>
        <div className={`flex-1 px-3 py-1.5 text-[10px] ${mutedTxt}`}>
          {/* `count` es obligatorio: sin él i18next no elige entre singular y
              plural y la clave sale sin traducir. */}
          {conRotura > 0
            ? t('planning.rotura.resumenConRotura', { count: conRotura, total: series.length })
            : t('planning.rotura.resumenSinRotura', { count: series.length })}
        </div>
      </div>

      {visibles.map(serie => (
        <FilaSerie key={serie.clave} serie={serie} track={track} dark={dark}
                   locale={i18n.language} fmtArt={fmtArt} t={t} bdr={bdr}
                   mutedTxt={mutedTxt} mainTxt={mainTxt} />
      ))}

      {/* El pie dice las dos cosas que no se ven mirando la franja. */}
      <div className={`flex items-center justify-between gap-3 px-3 py-1 text-[10px] ${mutedTxt}`}>
        <span>{t('planning.rotura.alcance')}</span>
        {ocultas > 0 && <span className="shrink-0">{t('planning.rotura.masSeries', { count: ocultas })}</span>}
      </div>
    </div>
  )
}

function FilaSerie({ serie, track, dark, locale, fmtArt, t, bdr, mutedTxt, mainTxt }: {
  serie: SerieComponente
  track: TimeTrack<string>
  dark: boolean
  locale: string
  fmtArt: (code: string) => string
  t: (key: string, opts?: Record<string, unknown>) => string
  bdr: string
  mutedTxt: string
  mainTxt: string
}) {
  const tramos = tramosDeSerie(serie, track.rangeStart)
  const rotura = serie.primeraRotura

  // `track.pos` trata el final como día INCLUSIVO —así pinta las barras de las
  // órdenes—, y aquí el tramo acaba donde empieza el siguiente. Un día menos,
  // o los dos tramos se solaparían un día.
  const posTramo = (desde: Date, hasta: Date | null) =>
    track.pos(desde, hasta ? new Date(hasta.getTime() - MS_DIA) : track.rangeEnd)

  const posRotura = rotura?.fecha ? track.pos(rotura.fecha, rotura.fecha) : null
  // Cerca del borde derecho la etiqueta se sale: se cuelga hacia la izquierda.
  const etiquetaALaIzquierda = (posRotura?.left ?? 0) > 62

  return (
    <div className={`flex border-b last:border-0 ${bdr}`}>
      <div className={`shrink-0 px-3 py-1.5 border-r ${bdr} flex flex-col justify-center gap-0.5`}
           style={{ width: INFO_COL_WIDTH }}>
        <div className="flex items-baseline gap-1.5 overflow-hidden">
          <span className={`text-[11px] font-bold tabular-nums shrink-0 ${mainTxt}`}>
            {fmtArt(serie.itemid)}
          </span>
          <span className={`text-[10px] truncate ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
            {serie.nombre ?? ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* La variante va escrita: el mismo código con dos colores son dos
              stocks, y sin el rótulo las dos filas parecerían repetidas. */}
          <span className={`text-[9px] uppercase tracking-wide truncate ${mutedTxt}`}>
            {[serie.empresa, serie.almacen, serie.color, serie.estilo].filter(Boolean).join(' · ')}
          </span>
          <span className={`text-[9px] tabular-nums ${mutedTxt}`}>
            {t('planning.rotura.stockInicial', { uds: fmtUds(serie.stockInicial) })}
          </span>
        </div>
      </div>

      <CarrilTrack
        boundaries={track.boundaries}
        todayX={track.todayX}
        dark={dark}
        className="flex-1 relative min-w-0 overflow-hidden"
        style={{ height: 34 }}
      >
        {tramos.map((tramo, i) => {
          const pos = posTramo(tramo.desde, tramo.hasta)
          if (!pos) return null
          return (
            <div
              key={i}
              className={`absolute top-2.5 h-3 rounded-[2px] ${
                tramo.rotura
                  ? 'bg-red-500'
                  : (dark ? 'bg-emerald-500/60' : 'bg-emerald-400')
              }`}
              style={{ left: `${pos.left}%`, width: `calc(${pos.width}% - 1px)`, minWidth: 2 }}
              title={[
                tramo.prodid ?? t('planning.rotura.stockDePartida'),
                fmtDateShortLocale(tramo.desde, locale),
                t(tramo.rotura ? 'planning.rotura.tooltipRotura' : 'planning.rotura.tooltipCubre',
                  { uds: fmtUds(Math.abs(tramo.saldo)) }),
              ].join(' · ')}
            />
          )
        })}

        {/* El marcador: la fecha, la orden que la provoca y lo que falta. Es la
            respuesta, así que va escrita y no sólo en el color. */}
        {rotura?.fecha && posRotura && (
          <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${posRotura.left}%` }}>
            <div className="absolute inset-y-0 w-[1.5px] bg-red-500" />
            <div
              className={`absolute top-0.5 whitespace-nowrap text-[9px] font-bold px-1 py-px rounded
                ${etiquetaALaIzquierda ? 'right-1' : 'left-1'}
                ${dark ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-600'}`}
            >
              {t('planning.rotura.marcador', {
                fecha: fmtDateShortLocale(rotura.fecha, locale),
                orden: rotura.prodid,
                uds: fmtUds(rotura.faltan),
              })}
            </div>
          </div>
        )}

        {/* Cuándo deja de romper. Sin esto, la franja dice el problema y calla
            la solución, que es el dato con el que se decide. */}
        {serie.entradaCubre && (() => {
          const pos = track.pos(serie.entradaCubre.fecha, serie.entradaCubre.fecha)
          if (!pos) return null
          return (
            <div
              className="absolute bottom-0.5 pointer-events-none text-[9px] font-bold text-sky-500 whitespace-nowrap"
              style={{ left: `${pos.left}%` }}
              title={t('planning.rotura.entradaTooltip')}
            >
              ▲ {fmtDateShortLocale(serie.entradaCubre.fecha, locale)}
              {serie.entradaCubre.cantidad !== null && ` · ${fmtUds(serie.entradaCubre.cantidad)}`}
            </div>
          )
        })()}

        {/* Las órdenes sin fecha no caben en un eje, y esconderlas haría creer
            que no existen. */}
        {serie.sinFecha > 0 && (
          <div className={`absolute right-1 top-1 text-[9px] font-semibold px-1 rounded ${
            dark ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
          }`}>
            {t('planning.rotura.sinFecha', { count: serie.sinFecha })}
          </div>
        )}
      </CarrilTrack>
    </div>
  )
}
